import SearchBox from "@/common/SearchBox";
import { LayoutHeader } from "@/Header";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import {
  useBusinessPhotoAlbum,
  useBusinessPhotoDelete,
  useBusinessPhotoDownload,
  // useBusinessPhotoDownload,
} from "~/services/business/photo";
import { useState, useEffect, useMemo, useCallback, useRef, memo } from "react";
import { ImageViewer } from "@/ImageViewer";
import { Dialog } from "@/Dialog";
// [성능 개선] 썸네일 비활성화로 사용 안함
// import { VideoThumbnail } from "@/common/VideoThumbnail";
import { useVirtualizer, type VirtualItem } from "@tanstack/react-virtual";
import { type InfiniteData, useQueryClient } from "@tanstack/react-query";
import type { BusinessPhotoAlbumResponse } from "~/types/common";

export const Route = createFileRoute("/business/layout/gallery")({
  component: Gallery,
});

type MediaType = "image" | "video";

type GalleryMediaItem =
  BusinessPhotoAlbumResponse["data"]["props"][number]["datas"][number];

type GalleryGroup = BusinessPhotoAlbumResponse["data"]["props"][number];

type GalleryRow =
  | { type: "header"; key: string; label: string }
  | { type: "media"; key: string; items: GalleryMediaItem[] };

// URL 확장자로 미디어 타입 판단
const getMediaType = (url: string): MediaType => {
  const videoExtensions = [".mp4", ".mov", ".avi", ".webm", ".mkv", ".m4v", ".3gp"];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some((ext) => lowerUrl.includes(ext))
    ? "video"
    : "image";
};

// 메모이제이션된 미디어 아이템 컴포넌트
// [성능 개선] 동영상만 플레이스홀더 처리 (Canvas 썸네일 생성이 메모리 과다 사용)
// - 이미지: 원본 <img> 태그 사용 (lazy loading으로 최적화됨)
// - 동영상: 플레이스홀더 표시 (VideoThumbnail의 Canvas 디코딩이 ~32MB 사용)
const MediaItem = memo(({
  item,
  imageIndex,
  onSelect,
}: {
  item: GalleryMediaItem;
  imageIndex: number;
  onSelect: (image: { url: string; albumSeq: number }) => void;
}) => {
  const mediaType = getMediaType(item.fileUrl);
  const isThumbnailReady = (item.thumbnailStatus || "").toUpperCase() === "COMPLETE";
  const imagePreviewUrl = isThumbnailReady && item.thumbnailUrl ? item.thumbnailUrl : item.fileUrl;
  const videoPreviewUrl = isThumbnailReady && item.thumbnailUrl ? item.thumbnailUrl : null;

  return (
    <div
      className="relative cursor-pointer aspect-square overflow-hidden rounded-lg bg-gray-100"
      onClick={() => onSelect({ url: item.fileUrl, albumSeq: item.albumSeq })}
    >
      {mediaType === "video" ? (
        videoPreviewUrl ? (
          <img
            src={videoPreviewUrl}
            alt={`Gallery image ${imageIndex + 1}`}
            className="object-cover w-full h-full rounded-lg"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="flex justify-center items-center w-full h-full bg-gray-300 rounded-lg">
            <svg
              className="w-10 h-10 text-gray-500"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
            </svg>
          </div>
        )
      ) : (
        <img
          src={imagePreviewUrl}
          alt={`Gallery image ${imageIndex + 1}`}
          className="object-cover w-full h-full rounded-lg"
          loading="lazy"
          decoding="async"
        />
      )}
      {mediaType === "video" && (
        <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-20 rounded-lg pointer-events-none">
          <svg
            className="w-8 h-8 text-white opacity-80"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      )}
    </div>
  );
});

MediaItem.displayName = "MediaItem";

function Gallery() {
  const { history } = useRouter();
  const queryClient = useQueryClient();
  const parentRef = useRef<HTMLDivElement>(null);

  // 검색 키워드 상태
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [currentSearchKeyword, setCurrentSearchKeyword] = useState<string>("");
  const [isSearchTriggered, setIsSearchTriggered] = useState<boolean>(false);

  // Dialog 상태
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogTitle, setDialogTitle] = useState("");

  // 삭제 확인 Dialog 상태
  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);
  // 이미지 뷰어 상태
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedAlbumSeq, setSelectedAlbumSeq] = useState<number | null>(null);
  const [isImageViewerVisible, setIsImageViewerVisible] = useState(false);
  const [selectedImagesUrl, setSelectedImagesUrl] = useState<string | null>(
    null
  );
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);

  // API 호출 (검색 버튼 클릭 시 또는 화면 진입 시 호출)
  const {
    data: albumData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useBusinessPhotoAlbum({
    searchKeyword: currentSearchKeyword,
    enabled: isSearchTriggered,
  });

  // DELETE API 훅
  const deletePhotoMutation = useBusinessPhotoDelete();

  // DOWNLOAD API 훅
  const downloadPhotoMutation = useBusinessPhotoDownload();

  const userAgent = navigator.userAgent;
  const isIOS = useMemo(() => {
    // 일반적인 iOS 감지 + 앱 내 웹뷰 감지
    return /MT_I/i.test(userAgent);
  }, [userAgent]);
  const isAndroid = useMemo(() => {
    // 일반적인 안드로이드 감지 + 앱 내 웹뷰 감지
    return /MT_A/i.test(userAgent);
  }, [userAgent]);

  // 페이지 경계가 같은 날짜에서 끊겨도 화면에는 날짜 그룹을 하나로 합친다.
  const groups = useMemo<GalleryGroup[]>(() => {
    const pageGroups = albumData?.pages.flatMap((page) => page.data.props) ?? [];

    return pageGroups.reduce<GalleryGroup[]>((merged, group) => {
      const previousGroup = merged[merged.length - 1];
      if (previousGroup?.type === group.type) {
        previousGroup.datas.push(...group.datas);
      } else {
        merged.push({ ...group, datas: [...group.datas] });
      }
      return merged;
    }, []);
  }, [albumData]);

  const allImages = useMemo(
    () => groups.flatMap((group) => group.datas),
    [groups]
  );

  // 모든 이미지 URL 메모이제이션
  const allImageUrls = useMemo(() => {
    return allImages.map((item) => item.fileUrl);
  }, [allImages]);

  // 날짜 헤더와 네 칸짜리 사진 행을 각각 가상화한다.
  const galleryRows = useMemo<GalleryRow[]>(() => {
    const rows: GalleryRow[] = [];

    groups.forEach((group) => {
      rows.push({ type: "header", key: `header-${group.type}`, label: group.type });
      for (let index = 0; index < group.datas.length; index += 4) {
        const items = group.datas.slice(index, index + 4);
        rows.push({
          type: "media",
          key: `media-${group.type}-${items[0]?.albumSeq ?? index}`,
          items,
        });
      }
    });

    return rows;
  }, [groups]);

  // 각 행의 ref를 저장할 Map
  const rowRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const imageLoadObserverRef = useRef<ResizeObserver | null>(null);

  // 가상화 설정 - 날짜 그룹 전체가 아니라 사진 한 행 단위로 DOM을 유지한다.
  const rowVirtualizer = useVirtualizer({
    count: galleryRows.length + (hasNextPage ? 1 : 0),
    getScrollElement: () => parentRef.current,
    estimateSize: (index: number) => {
      const row = galleryRows[index];
      if (!row) return 56;
      if (row.type === "header") return 40;

      const availableWidth = typeof window !== "undefined" ? window.innerWidth - 32 : 375;
      return availableWidth / 4 + 16;
    },
    overscan: 3, // 화면 밖에 미리 렌더링할 항목 수
    measureElement: (element: Element) => {
      // 실제 DOM 요소의 높이 측정
      if (!element) return 200;
      const height = element.getBoundingClientRect().height;
      return height > 0 ? height : 200;
    },
  });

  // 컴포넌트 마운트 시 한 번은 API 호출 (기본 데이터)
  useEffect(() => {
    setIsSearchTriggered(true);
  }, []);

  // 이미지/비디오 로드 후 높이 재측정을 위한 ResizeObserver 설정
  useEffect(() => {
    if (galleryRows.length === 0) return;

    // ResizeObserver로 각 행의 크기 변경 감지
    imageLoadObserverRef.current = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const element = entry.target as HTMLDivElement;
        const index = parseInt(element.dataset.index || '0', 10);
        if (!isNaN(index)) {
          // 높이 변경 시 가상화 라이브러리에 알림
          rowVirtualizer.measureElement(element);
        }
      });
    });

    // 모든 행 요소 관찰 시작
    rowRefs.current.forEach((el) => {
      if (imageLoadObserverRef.current) {
        imageLoadObserverRef.current.observe(el);
      }
    });

    // 초기 높이 측정 (이미지 로드 대기)
    const timeoutId = setTimeout(() => {
      rowRefs.current.forEach((el) => {
        rowVirtualizer.measureElement(el);
      });
    }, 100);

    return () => {
      if (imageLoadObserverRef.current) {
        imageLoadObserverRef.current.disconnect();
      }
      clearTimeout(timeoutId);
    };
  }, [galleryRows, rowVirtualizer]);

  const virtualRows = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    const lastVirtualRow = virtualRows[virtualRows.length - 1];
    if (
      !lastVirtualRow ||
      !hasNextPage ||
      isFetchingNextPage ||
      lastVirtualRow.index < galleryRows.length - 4
    ) {
      return;
    }

    void fetchNextPage();
  }, [
    fetchNextPage,
    galleryRows.length,
    hasNextPage,
    isFetchingNextPage,
    virtualRows,
  ]);

  // 이미지 뷰어 닫기
  const closeImageViewer = useCallback(() => {
    setIsImageViewerVisible(false);
    setSelectedImage(null);
    setSelectedAlbumSeq(null);
  }, []);

  // Gallery 전용 뒤로가기 처리 함수를 전역에 등록
  useEffect(() => {
    const userAgent = navigator.userAgent;
    const isAndroidApp = /MT_A/i.test(userAgent);

    if (isAndroidApp && typeof window !== "undefined") {
      // Gallery 전용 뒤로가기 처리 함수 (ImageViewer 열려있으면 닫기)
      window.customBackHandler = () => {
        console.log("business gallery 커스텀 뒤로가기 처리");
        if (isImageViewerVisible) {
          closeImageViewer();
        } else {
          // 기본 핸들러로 위임 (앱 종료 다이얼로그 표시)
          window.customBackHandler = null;
          window.fnHistoryBack();
        }
      };

      console.log("business gallery customBackHandler 등록");

      // 컴포넌트 언마운트 시 정리
      return () => {
        console.log("business gallery 언마운트 - customBackHandler 제거");
        window.customBackHandler = null;
      };
    }
  }, [isImageViewerVisible]);

  // Dialog 닫기 함수
  const handleDialogClose = () => {
    setIsDialogVisible(false);
    setIsImageViewerVisible(false);
    setDialogMessage("");
    setDialogTitle("");
  };

  // 삭제 확인 Dialog 닫기 함수
  const handleDeleteConfirmClose = () => {
    setIsDeleteConfirmVisible(false);
  };

  // 삭제 확인 Dialog에서 삭제 실행 함수
  const handleDeleteConfirm = () => {
    setIsDeleteConfirmVisible(false);

    if (!selectedAlbumSeq) {
      console.error("삭제할 이미지의 albumSeq가 없습니다.");
      setDialogTitle("삭제 실패");
      setDialogMessage("삭제할 이미지 정보를 찾을 수 없습니다.");
      setIsDialogVisible(true);
      return;
    }

    deletePhotoMutation.mutate(
      { albumSeq: selectedAlbumSeq },
      {
        onSuccess: (response) => {
          console.log("사진 삭제 성공:", response);
          queryClient.setQueriesData<InfiniteData<BusinessPhotoAlbumResponse>>(
            { queryKey: ["businessPhotoAlbum"] },
            (currentData) => {
              if (!currentData) return currentData;

              return {
                ...currentData,
                pages: currentData.pages.map((page) => ({
                  ...page,
                  data: {
                    ...page.data,
                    props: page.data.props
                      .map((group) => ({
                        ...group,
                        datas: group.datas.filter(
                          (item) => item.albumSeq !== selectedAlbumSeq
                        ),
                      }))
                      .filter((group) => group.datas.length > 0),
                  },
                })),
              };
            }
          );
          closeImageViewer();
          setDialogTitle("삭제 성공");
          setDialogMessage("사진이 성공적으로 삭제되었습니다.");
          setIsDialogVisible(true);
        },
        onError: (error) => {
          console.error("사진 삭제 실패:", error);
          setDialogTitle("삭제 실패");
          setDialogMessage("사진 삭제에 실패했습니다. 다시 시도해주세요.");
          setIsDialogVisible(true);
        },
      }
    );
  };

  // 개별 이미지 선택 - 이미지 뷰어 팝업 띄우기
  const toggleImageSelection = useCallback((image: { url: string; albumSeq?: number }) => {
    // 현재 인덱스 찾기 (allImages는 메모이제이션됨)
    const currentIndex = allImages.findIndex(
      (img) => img.albumSeq === image.albumSeq
    );

    setSelectedImage(image.url);
    setSelectedAlbumSeq(image.albumSeq || null);
    setSelectedImagesUrl(image.url);
    setCurrentImageIndex(currentIndex >= 0 ? currentIndex : 0);
    setIsImageViewerVisible(true);
  }, [allImages]);

  // 미디어 다운로드 (이미지/동영상)
  const handleImageDownload = () => {
    if (!selectedAlbumSeq || !selectedImagesUrl) {
      console.error("다운로드할 미디어의 albumSeq가 없습니다.");
      setDialogTitle("다운로드 실패");
      setDialogMessage("다운로드할 미디어 정보를 찾을 수 없습니다.");
      setIsDialogVisible(true);
      return;
    }

    // 현재 선택된 미디어 타입 확인
    const mediaType = getMediaType(selectedImagesUrl);
    const isVideo = mediaType === "video";

    if (isAndroid) {
      // 안드로이드에서는 네이티브 함수 사용
      if (isVideo) {
        // 동영상 다운로드
        if ((window.mung as any).downloadVideo) {
          (window.mung as any).downloadVideo(selectedImagesUrl);
        } else {
          // downloadVideo가 없으면 downloadImage 사용 (폴백)
          window.mung.downloadImage(selectedImagesUrl);
        }
      } else {
        window.mung.downloadImage(selectedImagesUrl);
      }
    } else if (isIOS) {
      // iOS에서는 네이티브 함수 사용
      if (isVideo) {
        window.webkit?.messageHandlers?.mung?.postMessage([
          "downloadVideo",
          selectedImagesUrl,
        ]);
      } else {
        window.webkit?.messageHandlers?.mung?.postMessage([
          "downloadImage",
          selectedImagesUrl,
        ]);
      }
    } else {
      // 웹에서는 API를 통해 다운로드
      downloadPhotoMutation.mutate(
        { albumSeq: selectedAlbumSeq },
        {
          onSuccess: (response) => {
            console.log("미디어 다운로드 정보 조회 성공:", response);
            try {
              // 응답이 직접 미디어 데이터인 경우 (Blob)
              if (response instanceof Blob) {
                const downloadUrl = window.URL.createObjectURL(response);
                const extension = isVideo ? ".mp4" : ".jpg";
                const fileName = `${isVideo ? "video" : "image"}-${currentImageIndex + 1}-${Date.now()}${extension}`;
                const link = document.createElement("a");
                link.href = downloadUrl;
                link.download = fileName;
                link.target = "_blank";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);
                return;
              }
            } catch (error) {
              console.error("다운로드 처리 중 오류:", error);
              setDialogTitle("다운로드 실패");
              setDialogMessage("다운로드 처리 중 오류가 발생했습니다.");
              setIsDialogVisible(true);
            }
          },
          onError: (error) => {
            console.error("미디어 다운로드 실패:", error);
            setDialogTitle("다운로드 실패");
            setDialogMessage(
              "미디어 다운로드에 실패했습니다. 다시 시도해주세요."
            );
            setIsDialogVisible(true);
          },
        }
      );
    }
  };

  // 이미지 삭제
  const handleImageDelete = () => {
    if (!selectedAlbumSeq) {
      console.error("삭제할 이미지의 albumSeq가 없습니다.");
      setDialogTitle("삭제 실패");
      setDialogMessage("삭제할 이미지 정보를 찾을 수 없습니다.");
      setIsDialogVisible(true);
      return;
    }

    // 삭제 확인 Dialog 표시
    setIsDeleteConfirmVisible(true);
  };

  const handleSearch = (keyword: string) => {
    parentRef.current?.scrollTo({ top: 0 });
    setCurrentSearchKeyword(keyword);
    setIsSearchTriggered(true);
  };

  // 날짜별 일괄 선택
  // const toggleDateSelection = (images: GalleryItem["images"]) => {
  //   const imageUrls = images.map((img) => img.url);
  //   const allSelected = imageUrls.every((url) => selectedImages.has(url));

  //   const newSelected = new Set(selectedImages);
  //   if (allSelected) {
  //     imageUrls.forEach((url) => newSelected.delete(url));
  //   } else {
  //     imageUrls.forEach((url) => newSelected.add(url));
  //   }
  //   setSelectedImages(newSelected);
  // };

  // 선택한 이미지 다운로드 (비즈니스용 갤러리는 단일 이미지 다운로드만 지원)
  // const downloadSelectedImages = async () => {
  //   try {
  //     for (const url of selectedImages) {
  //       const response = await fetch(url);
  //       const blob = await response.blob();
  //       const downloadUrl = window.URL.createObjectURL(blob);
  //       const a = document.createElement("a");
  //       a.href = downloadUrl;
  //       a.download = `image-${Date.now()}.jpg`;
  //       document.body.appendChild(a);
  //       a.click();
  //       window.URL.revokeObjectURL(downloadUrl);
  //       document.body.removeChild(a);
  //     }
  //     // 다운로드 완료 후 선택 모드 해제
  //     setIsSelectMode(false);
  //     setSelectedImages(new Set());
  //   } catch (error) {
  //     console.error("다운로드 중 오류 발생:", error);
  //   }
  // };

  // 로딩 상태 처리
  if (isLoading) {
    return (
      <>
        <LayoutHeader title={"사진첩"} hasBackBtn hasFootPrint={false} />
        <div className="flex flex-col flex-auto bg-[#F5F5F5] items-center justify-center">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      </>
    );
  }

  // 에러 상태 처리
  if (error) {
    return (
      <>
        <LayoutHeader title={"사진첩"} hasBackBtn hasFootPrint={false} />
        <div className="flex flex-col flex-auto bg-[#F5F5F5] items-center justify-center">
          <div className="text-red-500">
            데이터를 불러오는 중 오류가 발생했습니다.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {!selectedImage && <LayoutHeader title={"사진첩"} hasFootPrint={false} />}
      <div className="flex flex-col flex-auto bg-[#F5F5F5]">
        {/* 헤더 */}
        <div className="flex justify-end items-center px-4 py-3">
          <SearchBox
            searchText={searchKeyword}
            setSearchText={setSearchKeyword}
            onSearch={handleSearch}
            placeholder="반려동물 이름 또는 보호자 이름으로 검색"
          />
        </div>
        <div className="flex justify-end items-center px-4">
          <div className="flex gap-2">
            <button
              onClick={() => history.push("/business/layout/gallery-add")}
              className="text-gray-600"
            >
              + 추가
            </button>
          </div>
        </div>

        {/* 갤러리 목록 - 가상화 적용 */}
        <div
          ref={parentRef}
          className="overflow-auto flex-1"
        >
          {groups.length > 0 ? (
            <div
              className="relative"
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
              }}
            >
              {virtualRows.map((virtualRow: VirtualItem) => {
                const row = galleryRows[virtualRow.index];
                return (
                  <div
                    key={row?.key ?? `loader-${virtualRow.key}`}
                    data-index={virtualRow.index}
                    ref={(el) => {
                      if (el) {
                        rowRefs.current.set(virtualRow.index, el);
                        // ResizeObserver에 추가
                        if (imageLoadObserverRef.current) {
                          imageLoadObserverRef.current.observe(el);
                        }
                        // 초기 높이 측정
                        requestAnimationFrame(() => {
                          rowVirtualizer.measureElement(el);
                        });
                      } else {
                        rowRefs.current.delete(virtualRow.index);
                      }
                    }}
                    className="absolute top-0 left-0 w-full px-4"
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    {!row ? (
                      <div className="py-4 text-center text-sm text-gray-500">
                        {isFetchingNextPage ? "사진을 불러오는 중..." : ""}
                      </div>
                    ) : row.type === "header" ? (
                      <div className="py-2">
                        <span className="text-sm text-gray-600">{row.label}</span>
                      </div>
                    ) : (
                      <div className="grid grid-cols-4 gap-2 pb-2 sm:grid-cols-4">
                        {row.items.map((item, imageIndex) => (
                          <MediaItem
                            key={item.albumSeq}
                            item={item}
                            imageIndex={imageIndex}
                            onSelect={toggleImageSelection}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">
              검색 결과가 없습니다.
            </div>
          )}
        </div>
      </div>
      {/* 이미지 뷰어 팝업 */}
      {selectedImage && (
        <ImageViewer
          imageUrl={selectedImage}
          isVisible={isImageViewerVisible}
          onClose={closeImageViewer}
          onDownload={handleImageDownload}
          onDelete={handleImageDelete}
          images={allImageUrls}
          currentIndex={currentImageIndex}
          mediaType={getMediaType(selectedImage)}
          defaultMutedList={allImages.map((item) => item.muteYn === "Y")}
          onImageChange={(index) => {
            setCurrentImageIndex(index);
            if (allImages[index]) {
              setSelectedImage(allImages[index].fileUrl);
              setSelectedAlbumSeq(allImages[index].albumSeq);
              setSelectedImagesUrl(allImages[index].fileUrl);
            }
          }}
        />
      )}
      {/* 성공/실패 Dialog */}
      <Dialog visible={isDialogVisible} onClose={handleDialogClose}>
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">{dialogTitle}</h3>
          <p className="mb-4 text-gray-600">{dialogMessage}</p>
          <button
            onClick={handleDialogClose}
            className="px-4 py-2 text-white bg-orange-500 rounded-lg hover:bg-orange-600"
          >
            확인
          </button>
        </div>
      </Dialog>
      {/* 삭제 확인 Dialog */}
      <Dialog
        visible={isDeleteConfirmVisible}
        onClose={handleDeleteConfirmClose}
      >
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">사진 삭제</h3>
          <p className="mb-4 text-gray-600">
            정말로 이 사진을 삭제하시겠습니까? 삭제된 사진은 복구할 수 없습니다.
          </p>
          <p className="mb-4 text-gray-600">
            삭제된 사진은 복구할 수 없습니다.
          </p>
          <div className="flex gap-3">
            <button
              className="flex-1 py-2 font-semibold text-gray-700 bg-white rounded-lg border border-gray-300"
              onClick={handleDeleteConfirmClose}
            >
              취소
            </button>
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-lg"
              onClick={handleDeleteConfirm}
            >
              삭제
            </button>
          </div>
          {/* <div className="flex gap-2 justify-center">
            <button
              onClick={handleDeleteConfirmClose}
              className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              취소
            </button>
            <button
              onClick={handleDeleteConfirm}
              className="px-4 py-2 text-white bg-orange-500 rounded-lg hover:bg-orange-600"
            >
              삭제
            </button>
          </div> */}
        </div>
      </Dialog>
    </>
  );
}
